import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchAssetReferralById,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { errorHandler, hasRole } from "utils/helper";
import { useAuth } from "context/AuthContext";
import {
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
  Referral,
  Roles,
} from "types";
import { AddAsserSuspectsMappingPopup } from "components/pop-up/AddAssetSuspectMapping";
import { AddInvestigationDecisionPopup } from "components/pop-up/AddInvestigationDecision";
import { ProsecutoChoiceRespondBackPopup } from "components/pop-up/ProsecutorChoiceRespondBack";
import InvestigationForm from "components/card/InvestigationForm";
import InvestigationDisplay from "components/card/InvestigationDisplay";
import SuspectMappingDisplay from "components/card/SuspectMappingDisplay";

interface FileDetails {
  file_name: string;
  url: string;
  uploaded_at: string;
}

interface AssetReferralInvestigationProps {
  referralId: string;
  isAssetCoordinator: boolean;
  investigationData?: {
    investigation_summary?: string;
    investigation_summary_file?: File | null;
    investigation_summary_file_details?: FileDetails[];
  };
  referral?: Referral | null;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onInvestigationCreated?: (newInvestigation: any) => void;
  setReferralData?: React.Dispatch<React.SetStateAction<Referral | null>>;
  fileRowProps: any;
}

export default function AssetReferralInvestigation({
  referralId,
  investigationData,
  referral,
  setActiveTab,
  onInvestigationCreated,
  setReferralData,
  fileRowProps,
  isAssetCoordinator,
}: AssetReferralInvestigationProps) {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSuspectPopupOpen, setIsSuspectPopupOpen] = useState(false);
  const [isDecisionPopupOpen, setIsDecisionPopupOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [choiceRespondBackPopupOpen, setChoiceRespondBackPopupOpen] =
    useState(false);

  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const hasExistingData =
    investigationData?.investigation_summary ||
    investigationData?.investigation_summary_file_details;

  const handleApproveChoice = async () => {
    try {
      setIsApproving(true);
      const formData = new FormData();
      formData.append("action_status", "Closed by prosecutor");

      const response = await updateAssetReferral(referralId, formData);
      showTooltip(t("prosecutor_choice_approved_successfully"), "success");

      if (setReferralData && referralId) {
        const updatedData = await fetchAssetReferralById(referralId);
        setReferralData(updatedData);
        onInvestigationCreated?.(updatedData);
        setActiveTab?.("Investigation");
      }
    } catch (error) {
      showTooltip(t("failed_to_approve_investigation"), "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleForwardToCoordinator = async () => {
    try {
      const formData = new FormData();
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR,
      );
      formData.append("is_the_investigation_finished", true.toString());

      const response = await updateAssetReferral(
        referral?.id as string,
        formData,
      );
      showTooltip(t("referral_forwarded_successfully"), "success");

      if (setReferralData && referralId) {
        const updatedData = await fetchAssetReferralById(referralId);
        setReferralData(updatedData);
        onInvestigationCreated?.(updatedData);
        setActiveTab?.("Investigation");
      }
    } catch (error) {
      showTooltip(t("failed_to_forward_to_coordinator"), "error");
    }
  };

  const handleInvestigationSubmit = async (summary: string, files: File[]) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("investigation_summary", summary);

      files.forEach((file) => {
        formData.append("investigation_summary_file", file);
      });

      const response = await updateAssetReferral(referralId, formData);
      showTooltip(t("investigation_details_updated_successfully"), "success");

      setIsEditing(false);
      onInvestigationCreated?.(response);
      setReferralData?.(response);
      setActiveTab?.("Investigation");
    } catch (error) {
      showTooltip(errorHandler(error as any), "error");
    } finally {
      setLoading(false);
    }
  };

  // Determine which action buttons to show
  const showEditButton =
    isAssetProsecutor && !referral?.prosecutor_decision_choice;
  const showFinishInvestigationButton =
    isAssetProsecutor && !referral?.prosecutor_decision_choice;
  const showForwardButton =
    isAssetProsecutor &&
    referral?.prosecutor_decision_choice === "do_not_proceed" &&
    !referral?.is_the_investigation_finished &&
    referral?.forward_status !==
      AssetReferralForwardingStatusEnum.FORWARDED_TO_COORDINATOR;
  const showCoordinatorActions =
    isAssetCoordinator &&
    referral?.is_the_investigation_finished &&
    referral?.action_status?.toLocaleLowerCase() !==
      AssetReferralActionStatusEnum.CLOSED_BY_PROSECUTOR.toLocaleLowerCase();

  // If in edit mode, show the form
  if (isEditing) {
    return (
      <div className="ml-1 mr-4 space-y-6">
        <InvestigationForm
          initialSummary={investigationData?.investigation_summary}
          onSubmit={handleInvestigationSubmit}
          onCancel={() => setIsEditing(false)}
          isSubmitting={loading}
          submitButtonText={hasExistingData ? "Update" : "Submit"}
          showCancelButton={true}
        />
      </div>
    );
  }

  // Default view - same for all users
  return (
    <div className="ml-1 mr-4 space-y-6">
      <div className="flex gap-2 w-full">
        {/* Investigation Details Card */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-4 w-fit max-w-[50%]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Investigation Information</h3>
            <div className="flex gap-2">
              {showEditButton && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors hover:bg-gray-100"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit
                </button>
              )}
              {showFinishInvestigationButton && (
                <button
                  type="button"
                  onClick={() => setIsDecisionPopupOpen(true)}
                  className="bg-[#212121] text-white px-4 py-1 rounded-md"
                >
                  Finish Investigation
                </button>
              )}
              {showForwardButton && (
                <button
                  onClick={handleForwardToCoordinator}
                  className="px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-700 disabled:opacity-60"
                >
                  Forward To Coordinator
                </button>
              )}
            </div>
          </div>

          <InvestigationDisplay
            investigationSummary={investigationData?.investigation_summary}
            investigationFiles={
              investigationData?.investigation_summary_file_details
            }
            prosecutorDecisionSummary={referral?.prosecutor_decsion_summary}
            prosecutorDecisionFiles={
              referral?.prosecutor_decision_not_to_prosecute_documents
            }
            fileRowProps={fileRowProps}
            administrative_decision_date={
              referral?.administrative_decision_date
            }
            administrative_decision_documents={
              referral?.administrative_decision_documents
            }
            administrative_decision_summary={
              referral?.administrative_decision_summary
            }
            application_type={referral?.application_type}
            suspect_asset_counts={referral?.suspect_asset_counts ?? []}
          />

          {showCoordinatorActions && (
            <div className="mt-3 flex gap-3">
              <button
                onClick={() => setChoiceRespondBackPopupOpen(true)}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                Reject
              </button>
              <button
                onClick={handleApproveChoice}
                disabled={isApproving}
                className="px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-700 disabled:opacity-60 transition-colors"
              >
                {isApproving ? "Processing..." : "Approve"}
              </button>
            </div>
          )}
        </div>

        {/* Suspect Mapping Card */}
        <div className="bg-white shadow-md rounded-lg p-6 mt-4 w-6/12">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <div className="ml-4">
                <h3 className="text-lg font-semibold">Suspect Information</h3>
                <p className="referral-form-description">
                  {referral?.suspect_asset_counts?.length
                    ? "View suspects associated with this investigation"
                    : "No suspects added yet"}
                </p>
              </div>
            </div>

            {isAssetProsecutor && (
              <button
                type="button"
                onClick={() => setIsSuspectPopupOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-black-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Suspect Mapping
              </button>
            )}
          </div>

          <SuspectMappingDisplay
            suspectAssetCounts={referral?.suspect_asset_counts || []}
            totalAssets={referral?.assets?.length || 0}
            // showAddButton={
            //   isAssetProsecutor && !referral?.suspect_asset_counts?.length
            // }
            onAddSuspect={() => setIsSuspectPopupOpen(true)}
            isAssetProsecutor={isAssetProsecutor}
          />
        </div>
      </div>

      {/* Popups */}
      <AddAsserSuspectsMappingPopup
        isOpen={isSuspectPopupOpen}
        onClose={() => setIsSuspectPopupOpen(false)}
        referralId={referralId}
        existingSuspects={
          referral?.suspect_asset_counts?.flatMap(
            (item) => item.asset_suspects,
          ) || []
        }
        existingAssets={
          referral?.suspect_asset_counts?.flatMap((item) => item.assets) || []
        }
        setReferralData={setReferralData}
        referral={referral}
      />

      <AddInvestigationDecisionPopup
        isOpen={isDecisionPopupOpen}
        onClose={() => setIsDecisionPopupOpen(false)}
        referralId={referralId}
        referral={referral as Referral}
        setActiveTab={setActiveTab}
      />

      <ProsecutoChoiceRespondBackPopup
        isOpen={choiceRespondBackPopupOpen}
        onClose={() => setChoiceRespondBackPopupOpen(false)}
        referralId={referralId}
        setActiveTab={setActiveTab}
        onNewDataAdded={onInvestigationCreated}
      />
    </div>
  );
}
